(() => {
  const reduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Track per-section cleanup functions so theme-editor unload can fire them.
  const sectionCleanups = new WeakMap();

  // iPhones only show :active (the pressed look on buttons) when the page listens for
  // touches. This empty, passive listener turns that on without affecting scrolling.
  document.addEventListener('touchstart', () => {}, { passive: true });

  // ===== CR MONOGRAM (canvas ASCII grid, ambient churn only) =====
  // The grid animates on its own: a couple of cells re-roll their glyph every
  // ~500-850ms. That idle churn is the ONLY animation. There are no mouse, pointer
  // or touch handlers, nothing reacts to input, and the cells never leave their grid
  // positions, so a swipe starting on the mark scrolls the page like any static image.
  function initHeroMark(root) {
    const wrap = root.querySelector('.cr-mark-wrap');
    const canvas = root.querySelector('.cr-hero-mark');
    if (!wrap || !canvas) return null;
    const ctx = canvas.getContext('2d');

    let SIZE = wrap.offsetWidth;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    function setSize() {
      SIZE = wrap.offsetWidth;
      canvas.width = SIZE * dpr;
      canvas.height = SIZE * dpr;
      canvas.style.width = SIZE + 'px';
      canvas.style.height = SIZE + 'px';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    }

    let cells = [];
    let imgLoaded = false;
    let rafId = null;
    const chars = 'CRCRCR0123456789·/+-=*';

    // Glyph colors follow the seasonal theme: --text for the mark, --muted for the
    // cells that flicker. The theme class is already on <html> before this runs.
    const rootStyle = getComputedStyle(document.documentElement);
    const markColor = rootStyle.getPropertyValue('--text').trim() || '#111111';
    const flickerColor = rootStyle.getPropertyValue('--muted').trim() || '#7D634B';

    // Fit the artwork's ink box to the grid, centred. Measuring the ink (rather than
    // trusting the PNG's own margins) is what centres the mark and lets it run larger:
    // the glyph covers more cells at the same font size, so the grid stays crisp.
    function inkBox(img, raster) {
      const m = document.createElement('canvas');
      m.width = raster; m.height = raster;
      const mCtx = m.getContext('2d', { willReadFrequently: true });
      mCtx.fillStyle = '#fff';
      mCtx.fillRect(0, 0, raster, raster);
      const natW = img.naturalWidth || raster;
      const natH = img.naturalHeight || raster;
      const fit = Math.min(raster / natW, raster / natH);
      const dw = natW * fit, dh = natH * fit;
      const dx = (raster - dw) / 2, dy = (raster - dh) / 2;
      mCtx.drawImage(img, dx, dy, dw, dh);
      const d = mCtx.getImageData(0, 0, raster, raster).data;
      let x0 = raster, y0 = raster, x1 = -1, y1 = -1;
      for (let y = 0; y < raster; y++) {
        for (let x = 0; x < raster; x++) {
          const i = (y * raster + x) * 4;
          if ((d[i] + d[i + 1] + d[i + 2]) / 3 < 60) {
            if (x < x0) x0 = x;
            if (x > x1) x1 = x;
            if (y < y0) y0 = y;
            if (y > y1) y1 = y;
          }
        }
      }
      if (x1 < 0) return { sx: 0, sy: 0, sw: natW, sh: natH };
      return {
        sx: (x0 - dx) / fit,
        sy: (y0 - dy) / fit,
        sw: (x1 - x0 + 1) / fit,
        sh: (y1 - y0 + 1) / fit
      };
    }

    let ink = null;

    function buildCells(img) {
      setSize();
      const off = document.createElement('canvas');
      off.width = SIZE;
      off.height = SIZE;
      const offCtx = off.getContext('2d', { willReadFrequently: true });
      offCtx.fillStyle = '#fff';
      offCtx.fillRect(0, 0, SIZE, SIZE);

      if (!ink) ink = inkBox(img, 400);
      // Phones give the mark nearly the full grid; desktop keeps headroom so the
      // CTA stays above the fold on a 900px-tall screen.
      const FILL = window.innerWidth <= 720 ? 0.94 : 0.8;
      const target = SIZE * FILL;
      const scale = Math.min(target / ink.sw, target / ink.sh);
      const dW = ink.sw * scale, dH = ink.sh * scale;
      offCtx.drawImage(img, ink.sx, ink.sy, ink.sw, ink.sh, (SIZE - dW) / 2, (SIZE - dH) / 2, dW, dH);

      // Original cell density — denser grid felt cluttered, lost the institutional look.
      const STEP = Math.max(7, Math.floor(SIZE / 80));
      const data = offCtx.getImageData(0, 0, SIZE, SIZE).data;

      cells = [];
      for (let y = STEP / 2; y < SIZE; y += STEP) {
        for (let x = STEP / 2; x < SIZE; x += STEP) {
          const i = (Math.floor(y) * SIZE + Math.floor(x)) * 4;
          const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
          if (brightness < 60) {
            cells.push({
              ox: x, oy: y,
              char: chars[Math.floor(Math.random() * chars.length)],
              flicker: Math.random() < 0.02 ? Math.random() * 2 + 1 : 0
            });
          }
        }
      }
    }

    // Reduced-motion path: one flat pass, every cell at its grid position.
    function renderStatic() {
      ctx.clearRect(0, 0, SIZE, SIZE);
      const fontSize = Math.max(8, SIZE / 80);
      ctx.font = `${fontSize}px "Geist Mono", "JetBrains Mono", monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.globalAlpha = 0.92;
      ctx.fillStyle = markColor;
      for (let i = 0; i < cells.length; i++) {
        const c = cells[i];
        ctx.fillText(c.char, c.ox, c.oy);
      }
    }

    // Ambient churn. Every 30-50 frames (~500-850ms) two or three cells re-roll their
    // glyph, flash earth-toned, and wobble a fraction of a character before settling
    // back. Cells are drawn from their fixed grid position plus that decaying offset —
    // there is no cursor field and no burst, so the mark never spreads or scatters.
    let frame = 0;
    let lastAmbientFrame = 0;
    let nextAmbientGap = 36;
    const ambientJitter = new Map(); // cell index -> {dx, dy, decayUntilFrame}
    const JITTER_FRAMES = 14;

    function render() {
      frame++;
      if (!imgLoaded) {
        rafId = requestAnimationFrame(render);
        return;
      }

      ctx.clearRect(0, 0, SIZE, SIZE);
      const fontSize = Math.max(8, SIZE / 80);
      ctx.font = `${fontSize}px "Geist Mono", "JetBrains Mono", monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (cells.length > 0 && (frame - lastAmbientFrame) >= nextAmbientGap) {
        lastAmbientFrame = frame;
        nextAmbientGap = 30 + Math.floor(Math.random() * 21); // 30-50 frames
        const numCells = 2 + Math.floor(Math.random() * 2);   // 2-3 cells
        for (let g = 0; g < numCells; g++) {
          const idx = Math.floor(Math.random() * cells.length);
          cells[idx].char = chars[Math.floor(Math.random() * chars.length)];
          ambientJitter.set(idx, {
            dx: (Math.random() - 0.5) * fontSize * 0.6,
            dy: (Math.random() - 0.5) * fontSize * 0.6,
            decayUntilFrame: frame + JITTER_FRAMES
          });
        }
      }

      for (let i = 0; i < cells.length; i++) {
        const cell = cells[i];

        let ambDX = 0, ambDY = 0;
        const j = ambientJitter.get(i);
        if (j) {
          if (frame >= j.decayUntilFrame) {
            ambientJitter.delete(i);
          } else {
            const remain = (j.decayUntilFrame - frame) / JITTER_FRAMES;
            ambDX = j.dx * remain;
            ambDY = j.dy * remain;
          }
        }

        if (j) {
          ctx.fillStyle = flickerColor;
          ctx.globalAlpha = 0.78;
        } else if (cell.flicker > 0 && Math.sin(frame * 0.04 + cell.ox * 0.01) > 0.7) {
          ctx.fillStyle = flickerColor;
          ctx.globalAlpha = 0.7;
        } else {
          ctx.fillStyle = markColor;
          ctx.globalAlpha = 0.92;
        }
        ctx.fillText(cell.char, cell.ox + ambDX, cell.oy + ambDY);
      }

      rafId = requestAnimationFrame(render);
    }

    function renderFallback() {
      setSize();
      ctx.clearRect(0, 0, SIZE, SIZE);
      ctx.font = `bold ${SIZE / 6}px "Instrument Serif", Georgia, serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.globalAlpha = 0.92;
      ctx.fillStyle = markColor;
      ctx.fillText('CR', SIZE / 2, SIZE / 2);
    }

    function draw() {
      try {
        buildCells(img);
        ambientJitter.clear(); // indices refer to the old cell array
        if (reduced) renderStatic();
      } catch (e) {
        console.warn('camprigid hero: buildCells failed, rendering fallback', e);
        renderFallback();
      }
    }

    const monogramSrc = canvas.dataset.monogramSrc;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgLoaded = true;
      draw();
    };
    img.onerror = () => {
      console.warn('camprigid hero: monogram image failed to load, using text fallback');
      renderFallback();
    };
    img.src = monogramSrc;

    // Re-rasterise on resize so the grid stays sharp and centred at the new width.
    function onResize() {
      if (!imgLoaded) return;
      draw();
    }
    window.addEventListener('resize', onResize);

    if (!reduced) render();

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
    };
  }

  // ===== MOBILE NAV TOGGLE =====
  function initMobileToggle(root) {
    const toggle = root.querySelector('.cr-mobile-toggle');
    const menu = root.querySelector('.cr-mobile-menu');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', () => menu.classList.toggle('is-open'));
  }

  // ===== PDP GALLERY THUMB SWITCHING =====
  function initPdpGallery(root) {
    const main = root.querySelector('.cr-pdp-gallery-main');
    const mainImg = main && main.querySelector('img[data-pdp-main-image]');
    const frameNum = root.querySelector('.cr-pdp-frame-num');
    const thumbs = root.querySelectorAll('.cr-pdp-thumb');
    if (!main || !thumbs.length) return;
    const total = String(thumbs.length).padStart(2, '0');
    // Honor the section's frame_label_prefix setting (defaults to "Frame") instead of hardcoding it.
    const framePrefix = (frameNum && frameNum.dataset.prefix) || 'Frame';

    thumbs.forEach((t) => {
      t.addEventListener('click', () => {
        const img = t.dataset.img;
        const frame = t.dataset.frame || '01';
        thumbs.forEach((x) => x.classList.remove('is-active'));
        t.classList.add('is-active');
        if (frameNum) frameNum.textContent = `${framePrefix} ${frame} / ${total}`;
        if (img && mainImg) {
          main.classList.add('is-swapping');
          setTimeout(() => {
            mainImg.src = img;
            requestAnimationFrame(() => main.classList.remove('is-swapping'));
          }, 200);
        }
      });
    });
  }

  // ===== PDP NOTIFY-WHEN-RELEASED EMAIL EXPAND =====
  function initPdpNotify(root) {
    root.querySelectorAll('[data-cr-notify-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('aria-controls');
        const target = id && root.querySelector('#' + CSS.escape(id));
        if (!target) return;
        const isOpen = !target.hasAttribute('hidden');
        if (isOpen) {
          target.setAttribute('hidden', '');
          btn.setAttribute('aria-expanded', 'false');
        } else {
          target.removeAttribute('hidden');
          btn.setAttribute('aria-expanded', 'true');
          const input = target.querySelector('input[type="email"]');
          if (input) input.focus();
        }
      });
    });
  }

  // ===== STRIP SHOPIFY GENERATOR META =====
  // Removes <meta name="generator" content="Shopify"> after page parse.
  // Cosmetic — Shopify is still detectable via headers/CDN/checkout — but no point
  // volunteering the platform name in the response body. Done in JS because Shopify's
  // theme validator rejects any modification of {{ content_for_header }} in layout/theme.liquid.
  function stripGeneratorMeta() {
    document.querySelectorAll('meta[name="generator"]').forEach((el) => {
      const v = (el.getAttribute('content') || '').toLowerCase();
      if (v.indexOf('shopify') !== -1) el.remove();
    });
  }

  // ===== FORM SUBMIT DEBOUNCE =====
  // Disables submit buttons + blocks duplicate submissions for 2.5s. This catches
  // double-clicks and frustrates simple bots that hammer the same form.
  // Honeypot: any form with a filled `cr_hp_field` input is silently dropped — bots
  // typically fill every field they see, real users never see the hidden one.
  function initFormGuards(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const forms = scope.querySelectorAll('form');
    forms.forEach((form) => {
      // Only guard forms that include the honeypot (newsletter, notify, contact).
      // Cart form + product ATC form have NO honeypot — bypass entirely so the
      // capture-phase submit-button disable below doesn't interfere with the
      // browser constructing the cart's Checkout submission (was reloading /cart).
      if (!form.querySelector('input[name="cr_hp_field"]')) return;

      if (form.dataset.crGuarded) return;
      form.dataset.crGuarded = '1';

      form.addEventListener('submit', (e) => {
        const honeypot = form.querySelector('input[name="cr_hp_field"]');
        if (honeypot && honeypot.value) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return false;
        }
        if (form.dataset.crSubmitting === '1') {
          e.preventDefault();
          return false;
        }
        form.dataset.crSubmitting = '1';
        const submits = form.querySelectorAll('button[type="submit"], input[type="submit"]');
        submits.forEach((btn) => {
          btn.dataset.crOrigDisabled = btn.disabled ? '1' : '0';
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
        });
        // Re-enable after 2.5s in case the form failed validation client-side and
        // the user needs to retry. Real submissions will navigate away first.
        setTimeout(() => {
          form.dataset.crSubmitting = '';
          submits.forEach((btn) => {
            if (btn.dataset.crOrigDisabled !== '1') btn.disabled = false;
            btn.removeAttribute('aria-busy');
          });
        }, 2500);
      }, true);
    });
  }

  // ===== CART COUNT LISTENER =====
  function patchCartCount(count) {
    document.querySelectorAll('.cr-nav-cart').forEach(el => {
      el.textContent = `Cart · ${count}`;
    });
  }
  document.addEventListener('cart:updated', (e) => {
    const c = (e.detail && e.detail.cart && e.detail.cart.item_count);
    if (typeof c === 'number') patchCartCount(c);
  });


  // ===== INIT =====
  function initAll(root) {
    root = root || document;
    const cleanup = initHeroMark(root);
    if (cleanup && root.nodeType === 1) {
      sectionCleanups.set(root, cleanup);
    }
    initMobileToggle(root);
    initPdpGallery(root);
    initPdpNotify(root);
    initFormGuards(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAll(document);
      stripGeneratorMeta();
    });
  } else {
    initAll(document);
    stripGeneratorMeta();
  }

  // Theme editor: re-init when a section reloads
  document.addEventListener('shopify:section:load', (e) => {
    initAll(e.target);
  });

  // Theme editor: clean up rAF loops + listeners when a section is removed
  document.addEventListener('shopify:section:unload', (e) => {
    const cleanup = sectionCleanups.get(e.target);
    if (cleanup) {
      try { cleanup(); } catch (err) { console.warn('camprigid section cleanup failed', err); }
      sectionCleanups.delete(e.target);
    }
  });
})();
